import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Doc } from '~/types/user';

import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';

import { DocumentDownloadCell } from './DocumentsTableCells/DocumentDownloadCell';
import { DocumentExtensionCell } from './DocumentsTableCells/DocumentExtensionCell';
import { DocumentMobileCell } from './DocumentsTableCells/DocumentMobileCell';
import { DocumentNameCell } from './DocumentsTableCells/DocumentNameCell';

const columnHelper = createAppColumnHelper<Doc>();

export const useDocumentsTableColumns = () => {
  const { t } = useTranslation(['common', 'settings']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Title
        columnHelper.accessor('title', {
          id: ColumnIds.documentsName,
          header: t('settings:documents.headers.name'),
          cell: ({ getValue }) => <DocumentNameCell name={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 200 },
            isResizable: true,
          },
        }),

        // File extension
        columnHelper.accessor('extension', {
          id: ColumnIds.documentsExtension,
          header: t('settings:documents.headers.type'),
          enableGlobalFilter: false,
          enableSorting: false,
          cell: ({ getValue }) => <DocumentExtensionCell extension={getValue()} />,
          meta: {
            styles: { size: '80px' },
          },
        }),

        // Download
        columnHelper.display({
          id: ColumnIds.documentsDownload,
          header: t('common:datatable.download'),
          cell: ({ row: { original: document } }) => <DocumentDownloadCell document={document} />,
          meta: {
            styles: { size: 'max-content', minSize: 60, justify: 'center' },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.documentsMobile,
          cell: ({ row: { original: document } }) => <DocumentMobileCell document={document} />,
          meta: { mobile: true },
        }),
      ]),
    [t],
  );
};
